import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useParams } from '@tanstack/react-router';
import {
  Banner, Button, Card, Details, List, ListItem, Select, Switch, TopAppBar, useSnackbar,
} from '@meshble/molo';
import type { components } from '../api/schema';
import { api, must, mustOk } from '../lib/api';
import { localeIntl, t } from '../lib/lingua';
import { quandoRelativo } from '../lib/attivita';
import { BarraUtente } from '../shell';
import { usePermessi } from '../session';
import { Copy } from '../icons';
import { Anello, CaricoSezione } from '../brand';
import { ElencoToken, PROFILO, puoAmministrare, puoCreare, type Profilo } from './token-api';

/**
 * Gli assistenti dell'organizzazione (blocco 27, docs/research/08-mcp.md §7.6).
 *
 * Un assistente — Claude, per ora da Claude Code — lavora su Molo con i diritti
 * di chi lo collega, e qui si decide cosa gli si lascia fare: se può
 * collegarsi, e con quali tool. Le letture partono accese, azioni e interventi
 * spenti. Quello che nessun interruttore accende sta in fondo, con il motivo,
 * perché chi amministra sappia che non è una dimenticanza.
 */

type StatoTool = components['schemas']['StatoTool'];

/** Il tipo di un tool, nella lingua di chi guarda; uno sconosciuto resta com'è. */
function tipoTool(kind: string): string {
  const tipi: Record<string, string> = {
    lettura: t('Lettura'),
    azione: t('Azione'),
    intervento: t('Intervento, chiede conferma'),
  };
  return tipi[kind] ?? kind;
}

export function AssistentiPage() {
  const { slug } = useParams({ from: '/shell/orgs/$slug/assistenti' });
  const qc = useQueryClient();
  const avvisa = useSnackbar();
  const q = useQuery({
    queryKey: ['org-assistants', slug],
    queryFn: () => must(api.GET('/api/v1/orgs/{slug}/assistants', { params: { path: { slug } } })),
  });
  const invalida = () => qc.invalidateQueries({ queryKey: ['org-assistants', slug] });

  const generale = useMutation({
    mutationFn: (active: boolean) =>
      mustOk(api.PUT('/api/v1/orgs/{slug}/assistants', { params: { path: { slug } }, body: { active } })),
    onSuccess: (_, active) => {
      void invalida();
      avvisa(active ? t('Assistenti accesi') : t('Assistenti spenti: l’endpoint rifiuta tutti'));
    },
  });
  const tool = useMutation({
    // `admins` omesso lascia com'è la concessione agli admin: accendere o
    // spegnere un tool non la ritira, e concederla non lo riaccende.
    mutationFn: (v: { name: string; active: boolean; admins?: boolean }) =>
      mustOk(api.PUT('/api/v1/orgs/{slug}/assistants/tools/{name}', {
        params: { path: { slug, name: v.name } }, body: { active: v.active, admins: v.admins },
      })),
    onSuccess: () => void invalida(),
  });

  const d = q.data;
  const aree = raggruppa(d?.tools ?? []);

  return (
    <>
      <TopAppBar title={t('Assistenti')} actions={<BarraUtente slug={slug} />} />
      <CaricoSezione attiva={q.isPending} />
      <div className="md-content">
        <div className="md-stack" style={{ gap: 'var(--md-sys-spacing-5)' }}>
          {q.error && <Banner tone="error" title={t('Non riesco a leggere gli assistenti')}>{q.error.message}</Banner>}
          {d && (
            <>
              <Card variant="outlined">
                <div className="md-section-head">
                  <h2 className="md-title-medium">{t('Assistenti su questa organizzazione')}</h2>
                  <Switch
                    aria-label={t('Gli assistenti possono collegarsi')}
                    checked={d.active}
                    disabled={!d.canEdit || generale.isPending}
                    onCheckedChange={(v) => generale.mutate(v)}
                  />
                </div>
                <p className="md-body-medium" style={{ margin: 0 }}>
                  {d.active
                    ? t('Accesi: un assistente collegato con il token di una persona del team lavora qui con i suoi diritti, e fa solo quello che i tool accesi qui sotto permettono.')
                    : t('Spenti: l’indirizzo degli assistenti rifiuta chiunque, qualunque token porti.')}
                </p>
                <p className="md-body-small" style={{ margin: 0, color: 'var(--md-sys-color-on-surface-variant)' }}>
                  {t('Gli assistenti sono per chi lavora nel team: chi è invitato come «Limitato» non si collega. Le azioni sono del proprietario: un amministratore usa solo quelle che il proprietario gli concede qui sotto, e ogni cosa fatta da un assistente finisce nel registro con il nome della persona e del token.')}
                  {!d.canEdit && <> {t('Accenderli e spegnerli spetta al proprietario.')}</>}
                </p>
              </Card>

              <Collega slug={slug} endpoint={d.endpoint} acceso={d.active} />

              <Card variant="outlined">
                <h2 className="md-title-medium" style={{ margin: 0 }}>{t('Cosa può fare')}</h2>
                {tool.error && <Banner tone="error" title={t('Non ho cambiato il tool')}>{(tool.error as Error).message}</Banner>}
                {aree.map(([area, voci]) => (
                  <div key={area} className="md-stack">
                    <h3 className="md-title-small" style={{ margin: 0 }}>{area}</h3>
                    <List>
                      {voci.map((s) => (
                        <ListItem
                          key={s.name}
                          headline={s.title}
                          supporting={`${s.name} · ${tipoTool(s.kind)}${s.default ? '' : ` · ${t('scelto qui')}`}`}
                          trailing={
                            <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--md-sys-spacing-4)' }}>
                              {s.kind !== 'lettura' && (
                                <span style={{ display: 'flex', alignItems: 'center', gap: 'var(--md-sys-spacing-2)' }}>
                                  <span className="md-body-small" style={{ color: 'var(--md-sys-color-on-surface-variant)' }}>
                                    {t('Anche agli admin')}
                                  </span>
                                  <Switch
                                    aria-label={`${s.title}: ${s.admins ? t('anche agli admin') : t('solo l’owner')}`}
                                    checked={s.admins}
                                    disabled={!d.canEdit || tool.isPending || !s.active}
                                    onCheckedChange={(v) => tool.mutate({ name: s.name, active: s.active, admins: v })}
                                  />
                                </span>
                              )}
                              <Switch
                                aria-label={`${s.title}: ${s.active ? t('acceso') : t('spento')}`}
                                checked={s.active}
                                disabled={!d.canEdit || tool.isPending}
                                onCheckedChange={(v) => tool.mutate({ name: s.name, active: v })}
                              />
                            </div>
                          }
                        />
                      ))}
                    </List>
                  </div>
                ))}
                <Details summary={t('Quello che un assistente non fa mai')}>
                  <List>
                    {(d.never ?? []).map((e) => (
                      <ListItem key={e.what} headline={e.what} supporting={e.reason} />
                    ))}
                  </List>
                  <p className="md-body-small" style={{ margin: 0, color: 'var(--md-sys-color-on-surface-variant)' }}>
                    {t('Nessun interruttore le accende, per nessun ruolo: si fanno dal pannello, dove ogni pulsante chiede conferma e nessun testo scritto da altri sta in mezzo.')}
                  </p>
                </Details>
              </Card>

              <Card variant="outlined">
                <h2 className="md-title-medium" style={{ margin: 0 }}>{t('Collegamenti attivi')}</h2>
                <h3 className="md-title-small" style={{ margin: 0 }}>{t('claude.ai e Claude Desktop')}</h3>
                <CollegamentiOAuth slug={slug} />
                <h3 className="md-title-small" style={{ margin: 0 }}>{t('Con un token')}</h3>
                <ElencoToken
                  slug={slug}
                  soloProfili
                  vuoto={t('Nessun token con un profilo. Quelli per gli script stanno in Impostazioni.')}
                />
              </Card>
            </>
          )}
        </div>
      </div>
    </>
  );
}

/** Le aree nell'ordine in cui il catalogo le presenta. */
function raggruppa(tools: StatoTool[]): Array<[string, StatoTool[]]> {
  const out = new Map<string, StatoTool[]>();
  for (const t of tools) out.set(t.area, [...(out.get(t.area) ?? []), t]);
  return [...out.entries()];
}

/**
 * Collega Claude Code: crea un token con il profilo scelto e prepara il comando.
 *
 * Il comando porta il token dentro, e si vede una volta sola come ogni token.
 * Va incollato nel terminale, non in un `.mcp.json` del progetto: da lì finisce
 * in un commit.
 */
function Collega({ slug, endpoint, acceso }: { slug: string; endpoint: string; acceso: boolean }) {
  const ha = usePermessi(slug);
  const avvisa = useSnackbar();
  const qc = useQueryClient();
  const [profilo, setProfilo] = useState<Profilo>('lettura');
  const [comando, setComando] = useState('');

  const crea = useMutation({
    mutationFn: () =>
      must(api.POST('/api/v1/orgs/{slug}/tokens', {
        params: { path: { slug } },
        body: {
          label: `Claude Code — ${new Date().toLocaleDateString(localeIntl())}`,
          profile: profilo, days: 30,
        },
      })),
    onSuccess: (t) => {
      void qc.invalidateQueries({ queryKey: ['org-tokens', slug] });
      setComando(`claude mcp add --transport http molo-${slug} ${endpoint} --header "Authorization: Bearer ${t.value}"`);
    },
  });

  if (!puoCreare(ha)) return null;
  const amministra = puoAmministrare(ha);

  return (
    <Card variant="outlined">
      <h2 className="md-title-medium" style={{ margin: 0 }}>{t('Collega un assistente')}</h2>
      <h3 className="md-title-small" style={{ margin: 0 }}>{t('claude.ai e Claude Desktop')}</h3>
      <p className="md-body-medium" style={{ margin: 0 }}>
        {t('In Claude, Impostazioni › Connettori › Aggiungi un connettore personalizzato, e incolla l’indirizzo qui sotto. Claude ti porterà su Molo per il consenso: lì scegli cosa può fare, e il collegamento compare fra quelli attivi.')}
      </p>
      <h3 className="md-title-small" style={{ margin: 0 }}>Claude Code</h3>
      {!acceso && (
        <Banner tone="info" title={t('Gli assistenti sono spenti')}>
          {t('Il collegamento si crea lo stesso, ma funzionerà quando saranno accesi.')}
        </Banner>
      )}
      <p className="md-body-medium" style={{ margin: 0 }}>
        {t('L’indirizzo di questa organizzazione è {indirizzo}. Il collegamento è un token con i tuoi diritti, ristretti dal profilo, che dura trenta giorni.', {
          indirizzo: <code className="md-mono">{endpoint}</code>,
        })}
      </p>
      {comando ? (
        <div className="md-stack">
          <p className="md-body-medium" style={{ margin: 0 }}>
            {t('Incollalo nel terminale: contiene il token, e {nonLoRivedrai}. Non metterlo in un file del progetto, da lì finirebbe in un commit.', {
              nonLoRivedrai: <strong>{t('non lo rivedrai')}</strong>,
            })}
          </p>
          <pre className="md-code" style={{ margin: 0, whiteSpace: 'pre-wrap', wordBreak: 'break-all' }}>{comando}</pre>
          <div className="md-form__actions">
            <Button variant="tonal" icon={<Copy />} onClick={() => { void navigator.clipboard.writeText(comando); avvisa(t('Comando copiato')); }}>
              {t('Copia il comando')}
            </Button>
            <Button variant="text" onClick={() => setComando('')}>{t('Fatto')}</Button>
          </div>
        </div>
      ) : (
        <div className="md-form">
          {crea.error && <Banner tone="error" title={t('Non l’ho creato')}>{(crea.error as Error).message}</Banner>}
          <Select
            label={t('Profilo')}
            value={profilo}
            options={[
              { value: 'lettura', label: t('{profilo} — guarda, non cambia niente', { profilo: t(PROFILO.lettura) }) },
              ...(amministra
                ? [{ value: 'amministrazione', label: t('{profilo} — anche le azioni accese qui', { profilo: t(PROFILO.amministrazione) }) }]
                : []),
            ]}
            onChange={(e) => setProfilo(e.target.value as Profilo)}
          />
          <div className="md-form__actions">
            <Button variant="filled" icon={crea.isPending ? <Anello /> : undefined} disabled={crea.isPending} onClick={() => crea.mutate()}>
              {t('Crea il collegamento')}
            </Button>
          </div>
        </div>
      )}
    </Card>
  );
}

/**
 * I collegamenti OAuth: nascono dal consenso su /collega, si rinnovano da soli
 * finché l'assistente li usa, e da qui si revocano. Chi amministra vede tutti
 * quelli dell'organizzazione; gli altri i propri.
 */
function CollegamentiOAuth({ slug }: { slug: string }) {
  const qc = useQueryClient();
  const avvisa = useSnackbar();
  const q = useQuery({
    queryKey: ['org-assistant-connections', slug],
    queryFn: () => must(api.GET('/api/v1/orgs/{slug}/assistants/connections', { params: { path: { slug } } })),
  });
  const revoca = useMutation({
    mutationFn: (id: string) =>
      mustOk(api.DELETE('/api/v1/orgs/{slug}/assistants/connections/{id}', { params: { path: { slug, id } } })),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['org-assistant-connections', slug] });
      avvisa(t('Collegamento revocato: l’assistente dovrà chiedere di nuovo il consenso'));
    },
  });
  if (q.error) return <Banner tone="error" title={t('Non riesco a leggere i collegamenti')}>{q.error.message}</Banner>;
  const cc = q.data ?? [];
  if (cc.length === 0) {
    return (
      <p className="md-body-medium" style={{ margin: 0, color: 'var(--md-sys-color-on-surface-variant)' }}>
        {t('Nessuno. Si collegano da Claude, con l’indirizzo qui sopra.')}
      </p>
    );
  }
  return (
    <List>
      {cc.map((c) => (
        <ListItem
          key={c.id}
          headline={c.client}
          supporting={[
            c.profile === 'amministrazione' ? t(PROFILO.amministrazione) : t(PROFILO.lettura),
            c.email,
            c.lastUsedAt ? t('usato {quando}', { quando: quandoRelativo(c.lastUsedAt) }) : t('mai usato'),
          ].join(' · ')}
          trailing={
            <Button variant="text" disabled={revoca.isPending} onClick={() => revoca.mutate(c.id)}>{t('Revoca')}</Button>
          }
        />
      ))}
    </List>
  );
}
